import { useQuery } from "@tanstack/react-query";
import { api, type UsageResponse } from "@/lib/api/client";
import { formatNumber } from "@/lib/utils/format-number";

// Refresh interval: 5 minutes
const REFRESH_INTERVAL = 5 * 60 * 1000;

/**
 * Hook to fetch and auto-refresh usage data
 *
 * Features:
 * - Auto-refreshes every 5 minutes
 * - Refetches on window focus
 * - Returns usage status, counts, and period info
 */
export function useUsage() {
  const query = useQuery({
    queryKey: ["usage"],
    queryFn: () => api.usage.get(),
    staleTime: 60_000, // Consider stale after 1 minute
    refetchInterval: REFRESH_INTERVAL,
    refetchOnWindowFocus: true,
  });

  return query;
}

/**
 * Get the next tier name for upgrade prompts
 */
export function getNextTier(currentPlan: UsageResponse["plan"]): string | null {
  const tiers: Record<UsageResponse["plan"], string | null> = {
    free: "Team",
    team: "Professional",
    professional: "Business",
    business: null,
  };
  return tiers[currentPlan];
}

/**
 * Format a number with locale-aware formatting
 */
export function formatReportCount(count: number): string {
  return formatNumber(count);
}

/**
 * Format limit for display (handles unlimited)
 */
export function formatLimit(limit: number | null): string {
  if (limit === null) {
    return "unlimited";
  }
  return formatNumber(limit);
}
